import * as React from "react";
import { Typography, Table, Button, Icon } from "antd";
import DisplayFormSectionQualifier from "./DisplayFormSectionQualifier";
import DisplayFormQualifierAttribute from "./DisplayFormQualifierAttribute";
import { IReferenceValue } from "../../../model/form/ReferenceValue";
import { IFormInputValueValidationError } from "../../../model/validation/FormInputValueValidationError";
import { IFormSectionPresentationModel } from "../../../presenter/form/model/FormSectionPresentationModel";
import { IFormSectionQualifierPresentationModel } from "../../../presenter/form/model/FormSectionQualifierPresentationModel";
import { IFormQualifierAttributePresentationModel } from "../../../presenter/form/model/FormQualifierAttributePresentationModel";
import { FormDisplayConstants } from "../../../common/Constants";
import { IFormInputValuePresentationModel } from "../../../presenter/form/model/FormInputValuePresentationModel";
import MobileTableOverview from "../portal/MobileTableOverview";
import TooltipHeaderCell from "../app/TooltipHeaderCell";

export interface State {}

export interface Props {
  formSection: IFormSectionPresentationModel;
  translate: any;
  getReferenceValues?: any;
  referenceLoading?: boolean;
  referenceValueList?: IReferenceValue[];
  parentFormSection?: IFormSectionPresentationModel;
  isEditableForm: boolean;
  addRow?: any;
  deleteRow?: any;
  changeFormAttribute?: any;
  validationErrors?: IFormInputValueValidationError[];
  formQualifierValidationErrors?: number[];
  allowedFileTypes: string;
  isPortal: boolean;
}

export default class DisplayFormSection extends React.Component<Props, State> {
  render() {
    const {
      formSection,
      translate,
      getReferenceValues,
      referenceLoading,
      referenceValueList,
      parentFormSection,
      isEditableForm,
      addRow,
      deleteRow,
      changeFormAttribute,
      validationErrors,
      formQualifierValidationErrors,
      allowedFileTypes,
      isPortal
    } = this.props;

    const sortFormSectionQualifiersByOrderNumber = (
      formSectionQualifiers: IFormSectionQualifierPresentationModel[]
    ) => {
      return formSectionQualifiers.sort((a, b) => {
        return a.orderNumber - b.orderNumber;
      });
    };

    const sortAttributesByOrderNumberInsertDisplay = (
      formQualifierAttributes: IFormQualifierAttributePresentationModel[]
    ) => {
      return formQualifierAttributes.sort((a, b) => {
        return a.orderNumberInsertDisplay - b.orderNumberInsertDisplay;
      });
    };

    const sortFormInputValuesByFormAttributeId = (
      formInputValues: IFormInputValuePresentationModel[]
    ) => {
      return formInputValues.sort((a, b) => {
        return a.formAttributeId - b.formAttributeId;
      });
    };

    const renderFormSubsections = () => {
      let formSubsectionRenderList: JSX.Element[] = [];
      if (formSection.subsections) {
        formSection.subsections.sort((a, b) => {
          return a.orderNumber - b.orderNumber;
        });

        formSection.subsections.forEach((subsection) => {
          formSubsectionRenderList.push(
            <div key={subsection.id} id={subsection.id.toString()}>
              <DisplayFormSection
                key={subsection.id}
                formSection={subsection}
                translate={translate}
                getReferenceValues={getReferenceValues}
                referenceLoading={referenceLoading}
                referenceValueList={referenceValueList}
                parentFormSection={formSection}
                isEditableForm={isEditableForm}
                addRow={addRow}
                deleteRow={deleteRow}
                changeFormAttribute={changeFormAttribute}
                validationErrors={validationErrors}
                formQualifierValidationErrors={formQualifierValidationErrors}
                allowedFileTypes={allowedFileTypes}
                isPortal={isPortal}
              />
            </div>
          );
        });
      }

      return formSubsectionRenderList;
    };

    const renderFormSectionQualifiersInline = (
      formSection: IFormSectionPresentationModel
    ) => {
      let qualifierRenderList: JSX.Element[] = [];
      if (formSection.qualifiers) {
        let sortedFormSectionQualifiers =
          sortFormSectionQualifiersByOrderNumber(formSection.qualifiers);

        sortedFormSectionQualifiers.forEach((qualifier) => {
          qualifier.formSection = formSection;
          qualifierRenderList.push(
            <DisplayFormSectionQualifier
              key={qualifier.formSectionQualifierId}
              formSectionQualifier={qualifier}
              parentSection={formSection}
              translate={translate}
              getReferenceValues={getReferenceValues}
              referenceLoading={referenceLoading}
              referenceValueList={referenceValueList}
              isEditableForm={isEditableForm}
              changeFormAttribute={changeFormAttribute}
              validationErrors={validationErrors}
              formTypeCode={formSection.formTypeCode}
              formQualifierValidationErrors={formQualifierValidationErrors}
              allowedFileTypes={allowedFileTypes}
            />
          );
        });
      }
      return qualifierRenderList;
    };

    const checkFormSectionQualifierValidationError = (
      formSectionQualifier: IFormSectionQualifierPresentationModel
    ) => {
      let error: boolean | undefined = false;
      formSectionQualifier.attributes?.forEach((attribute) =>
        attribute.values.forEach((value) => {
          if (formQualifierValidationErrors?.includes(value.formQualifierId)) {
            error = true;
          }
        })
      );
      return error;
    };

    const renderQualifierTitle = (
      formSectionQualifier: IFormSectionQualifierPresentationModel,
      error?: boolean
    ) => {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column"
          }}
        >
          {formSectionQualifier.labelVisible && formSectionQualifier.label
            ? formSectionQualifier.label
            : formSectionQualifier.qualifierName}
          {error && (
            <div style={{ color: "red", fontSize: 11 }}>
              {translate("form_details.qualifier_validation_error")}
            </div>
          )}
        </div>
      );
    };
    const renderFormSectionQualifiersGrid = (
      formSection: IFormSectionPresentationModel
    ) => {
      let columns: any[] = [];
      let rows: any[] = [];
      let tableConfiguration = {};
      const isAggregated = isPortal && formSection.portalAggregate;
      if (formSection.qualifiers && formSection.qualifiers.length !== 0) {
        let sortedFormSectionQualifiers =
          sortFormSectionQualifiersByOrderNumber(formSection.qualifiers);

        rows = getFormSectionQualifierGridRows(formSection);

        sortedFormSectionQualifiers.forEach(
          (
            formSectionQualifier: IFormSectionQualifierPresentationModel,
            columnIndex: number
          ) => {
            let error =
              checkFormSectionQualifierValidationError(formSectionQualifier);
            columns.push({
              title: renderQualifierTitle(formSectionQualifier, error),
              dataIndex: formSectionQualifier.formSectionQualifierId,
              key: formSectionQualifier.formSectionQualifierId,
              width: formSectionQualifier.width,
              className: error ? "form-qualifier-error" : undefined,
              onHeaderCell: () => ({
                tooltip: formSectionQualifier.tooltip
              }),
              render: (text: string, record: any, rowIndex: number) =>
                renderFormQualifierAttributes(
                  formSectionQualifier,
                  rowIndex,
                  isAggregated,
                  columnIndex
                )
            });
          }
        );

        isEditableForm &&
          rows.length > 0 &&
          columns.push({
            key: "actions",
            fixed: "right",
            width: FormDisplayConstants.ACTIONS_COLUMN_WIDTH,
            render: (text: string, record: any, rowIndex: number) => {
              return (
                <div>
                  <Button
                    style={{ fontSize: 12 }}
                    type={"danger"}
                    onClick={(e) => deleteTableRowByRowIndex(rowIndex)}
                  >
                    <Icon type="close" />
                    {translate("form_details.delete_row_btn")}
                  </Button>
                </div>
              );
            }
          });

        if (formSection.maxNumber === -1 && isEditableForm) {
          tableConfiguration = {
            footer: () => (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  style={{ fontSize: 12 }}
                  onClick={(e) => addRow(formSection.id)}
                >
                  <Icon type="plus" />
                  {translate("form_details.add_row")}
                </Button>
              </div>
            )
          };
        }
        return (
          <>
            <Table
              {...tableConfiguration}
              className={"form-details-section-table"}
              key={formSection.id}
              columns={columns}
              dataSource={rows}
              pagination={false}
              bordered={true}
              locale={{ emptyText: translate("form_details.no_rows") }}
              scroll={{ x: true }}
              components={{ header: { cell: TooltipHeaderCell } }}
            />
            <MobileTableOverview
              className={"form-details-section-mobile-table"}
              columns={columns}
              rows={rows}
            />
          </>
        );
      }
    };

    const deleteTableRowByRowIndex = (rowIndex: number) => {
      let qualifierId = getFirstQualifierIdByRowIndex(rowIndex);

      deleteRow(qualifierId);
    };

    const getFirstQualifierIdByRowIndex = (rowIndex: number) => {
      if (
        formSection.qualifiers &&
        formSection.qualifiers[0] &&
        formSection.qualifiers[0].attributes &&
        formSection.qualifiers[0].attributes[0] &&
        formSection.qualifiers[0].attributes[0].values &&
        formSection.qualifiers[0].attributes[0].values[rowIndex]
      ) {
        return formSection.qualifiers[0].attributes[0].values[rowIndex]
          .formQualifierId;
      }
    };
    const renderFormQualifierAttributes = (
      formSectionQualifier: IFormSectionQualifierPresentationModel,
      rowIndex: number,
      isAggregated: boolean,
      columnIndex: number
    ) => {
      let attributeRenderList: JSX.Element[] = [];
      if (isAggregated && columnIndex === 0) {
        return <b>{translate("form_details.total")}</b>;
      }
      if (formSectionQualifier.attributes) {
        let sortedFormQualifierAttributes =
          sortAttributesByOrderNumberInsertDisplay(
            formSectionQualifier.attributes
          );
        sortedFormQualifierAttributes.forEach(
          (
            formQualifierAttribute: IFormQualifierAttributePresentationModel
          ) => {
            formQualifierAttribute.values =
              sortFormInputValuesByFormAttributeId(
                formQualifierAttribute.values
              );
            attributeRenderList.push(
              <DisplayFormQualifierAttribute
                key={formQualifierAttribute.id}
                translate={translate}
                attribute={formQualifierAttribute}
                getReferenceValues={getReferenceValues}
                referenceValueList={referenceValueList}
                isEditableForm={isEditableForm}
                rowIndex={rowIndex}
                changeFormAttribute={changeFormAttribute}
                validationErrors={validationErrors}
                formTypeCode={formSection.formTypeCode}
                qualifierCode={formSectionQualifier.qualifierCode}
                allowedFileTypes={allowedFileTypes}
                isAggregated={isAggregated}
              />
            );
          }
        );
      }
      return attributeRenderList;
    };

    const getFormSectionQualifierGridRows = (
      formSection: IFormSectionPresentationModel
    ) => {
      let rows: any[] = [];

      if (formSection.maxNumber !== -1) {
        for (let i = 0; i < formSection.maxNumber; i++) {
          rows.push({ key: i });
        }
      } else {
        let valuesLength = 0;
        formSection.qualifiers &&
          formSection.qualifiers.forEach((qualifier) => {
            qualifier.attributes &&
              qualifier.attributes.forEach((attribute) => {
                if (
                  attribute.values &&
                  attribute.values.length > valuesLength
                ) {
                  valuesLength = attribute.values.length;
                }
              });
          });

        for (let i = 0; i < valuesLength; i++) {
          rows.push({ key: i });
        }
      }
      return rows;
    };

    const getError = (formSectionId: number) => {
      return validationErrors?.find(
        (item) => item?.formSectionId === formSectionId
      );
    };
    const errorMessage = (formSectionId: number) => {
      const error = getError(formSectionId);
      if (error) {
        return translate(error.messageTranslationCode);
      }
      return " ";
    };

    return (
      <div className={"display-form-section-wrapper"} style={{ padding: 5 }}>
        {parentFormSection ? (
          <h4
            className={"display-form-section-title"}
            style={{ fontWeight: "normal", marginTop: 10 }}
          >
            {`
            ${
              formSection.orderNumberVisible
                ? `${parentFormSection.orderNumber}.${formSection.orderNumber}`
                : ""
            }
          ${formSection.labelVisible ? formSection.label : ""}
          `}
            <span style={{ color: "red" }}>
              {formSection.isRequiredFlagVisible ? "*" : ""}
            </span>
            <span style={{ color: "red" }}>{errorMessage(formSection.id)}</span>
          </h4>
        ) : (
          <h3 className={"display-form-section-title"}>
            {`${
              formSection.orderNumberVisible
                ? `${formSection.orderNumber}.`
                : ""
            }
          ${formSection.labelVisible ? formSection.label : ""}
          `}
            <span style={{ color: "red" }}>
              {formSection.isRequiredFlagVisible ? "*" : ""}
            </span>
            <span style={{ color: "red" }}>{errorMessage(formSection.id)}</span>
          </h3>
        )}

        {formSection.comment ? (
          <Typography.Text type="secondary" style={{ marginBottom: 7 }}>
            {formSection.comment}
          </Typography.Text>
        ) : null}

        {renderFormSubsections()}
        {formSection.maxNumber && formSection.maxNumber === 1
          ? renderFormSectionQualifiersInline(formSection)
          : renderFormSectionQualifiersGrid(formSection)}
      </div>
    );
  }
}
